import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { IChatConversation, INewConversationData, IOpenConversationManageUsersData } from '@fi-shared/fi-chat/fi-chat';
import { ILightWeightUser } from '@fi-shared/models';
import { Subject } from 'rxjs';
import { AppTextService } from '@fi-shared/app-text';

@Component({
    selector: 'fi-chat-conversation-list-container',
    template: `
        <mat-card class="fi-content-card">
          <div
            class="fi-content-card-top ContrastBackgroundColor hide-mobile"
            [class.forward-message]="forwardMessageMode"
            >
            <span>{{ getCardHeader() }}</span>
            <div class="fi-content-card-top-button-container">
              @if (!forwardMessageMode) {
                <button
                  mat-icon-button
                  class="fi-content-card-top-button"
                  (click)="onOpenNewConversation()"
                  >
                  <span class="fas fa-edit"></span>
                </button>
              }
              <button mat-icon-button class="fi-content-card-top-button" (click)="onCloseConversationList()">
                <span [class]="getCloseConversationListIconClass()"></span>
              </button>
            </div>
          </div>
          <mat-card-content #matCardContentElement (fiWhenScrolled)="onScroll()">
            <fi-chat-conversation-search (search)="onSearch($event)" />
        
            <fi-chat-conversation-list
              [updateSuggestionsList$]="updateSuggestionsList$.asObservable()"
              [searchValue]="searchValue"
              [isConversationMenuAvailable]="!forwardMessageMode"
              (openNewConversation)="onOpenNewConversation($event)"
              (openExistingConversation)="onOpenExistingConversation($event)"
              (closeConversation)="onCloseConversation($event)"
              (openManageUsersConversation)="onOpenManageUsersConversation($event)"
              (createNewGroupConversation)="onCreateNewGroupConversation($event)"
              />
            </mat-card-content>
          </mat-card>
        `,
    styleUrls: ['./fi-chat-conversation-list-container.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-chat-conversation-list-container' },
    standalone: false,
})
export class FiChatConversationListContainerComponent {
    @ViewChild('matCardContentElement', { read: ElementRef }) matCardContentElementRef: ElementRef;
    @Output() openExistingConversation = new EventEmitter<IChatConversation>();
    @Output() closeConversationList = new EventEmitter();
    @Output() openNewConversation = new EventEmitter<ILightWeightUser>();
    @Output() closeConversation = new EventEmitter<IChatConversation>();
    @Output() openManageUsersConversation = new EventEmitter<IOpenConversationManageUsersData>();
    @Output() createNewGroupConversation = new EventEmitter<INewConversationData>();
    searchValue = '';
    updateSuggestionsList$: Subject<void> = new Subject<void>();
    @Input() forwardMessageMode: boolean;

    constructor(private appTextService: AppTextService) {}

    onScroll() {
        const containerOffsetHeight: number = this.matCardContentElementRef.nativeElement.offsetHeight;
        const containerScrollTop: number = this.matCardContentElementRef.nativeElement.scrollTop;
        const containerScrollHeight: number = this.matCardContentElementRef.nativeElement.scrollHeight;
        if (Math.ceil(containerOffsetHeight + containerScrollTop) >= containerScrollHeight)
            this.updateSuggestionsList$.next();
    }

    getCardHeader(): string {
        if (this.forwardMessageMode) return this.appTextService.get('message.forward_to') + '...';

        return this.appTextService.get('chat.messaging');
    }

    getCloseConversationListIconClass() {
        if (this.forwardMessageMode) return 'fa fa-times';

        return 'fa fa-chevron-down';
    }

    onCreateNewGroupConversation($event: INewConversationData) {
        this.createNewGroupConversation.emit($event);
    }

    onOpenManageUsersConversation($event: IOpenConversationManageUsersData) {
        this.openManageUsersConversation.emit($event);
    }

    onCloseConversation($event: IChatConversation) {
        this.closeConversation.emit($event);
    }

    onSearch($event: string) {
        this.searchValue = $event;
    }

    onCloseConversationList() {
        this.closeConversationList.emit();
    }

    onOpenExistingConversation($event: IChatConversation) {
        this.openExistingConversation.emit($event);
    }

    onOpenNewConversation($event?: ILightWeightUser) {
        this.openNewConversation.emit($event);
    }
}
